/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/react';

import { labelStyles } from '@zapier/style-encapsulation';
import Typeahead from '../../forms/Typeahead';
import { FloatingMenu, FloatingMenuProps } from '../../forms/FloatingMenu';
import { type TextInputProps } from '@/components/forms/TextInput';

import { useAppSearchData } from './useAppSearchData';
import { ServiceIcon } from '../ServiceIcon';

const Styles = labelStyles('ServiceTypeahead', {
  serviceIcon: css`
    &[data-disabled] {
      opacity: 0.6;
    }
  `,
});

type ServiceTypeaheadProps = {
  /**
   * Force API requests to be sent to this hostname, instead of the current domain.
   * Most likely needed for applications running on a subdomain.
   */
  apiHostname?: string;
  /**
   * Accessibility label that describes this component.
   * This should be supplied if no corresponding <label> element is used.
   */
  'aria-label'?: string;
  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`.
   * Only renders when `isDisabled`.
   */
  disabledText?: string;
  /** Excludes upcoming apps from search results. */
  excludeUpcoming?: boolean;
  /** HTML `id` to use for the node. */
  id?: string;
  /** Optionally toggles the inclusion of services where the authenticated user is associated with an app as an admin. */
  includeIsAppAdmin?: boolean;
  /** Optionally toggles the inclusion of services where the authenticated user is associated with an app as a collaborator. */
  includeIsAppCollaborator?: boolean;
  /** Optional prop for setting the initially selected item. */
  initialSelectedItem?: any;
  /**
   * HTML `id` attribute for the `input` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;
  /** Disables the input and makes it non-interactive. */
  isDisabled?: boolean;
  /** `id` of the label that connects to this select field. */
  labelId?: string;
  /**
   * Accessibility label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel: string;
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: any) => void;
  /**
   * Callback that gets called when service search results are updated.
   */
  onResultsUpdate?: (searchQuery: string, results: any) => void;
  /**
   * Placeholder to render inside the select toggle when no item is selected.
   */
  placeholder: string;
  /** A render prop for creating the floating menu. */
  renderFloatingMenu?: (props: FloatingMenuProps) => React.ReactElement;
  /** The total number of results to show in the dropdown. */
  resultsLimit?: number;
  /**
   * An optional function to hold an external reference to Downshift's clearSelection prop.
   */
  setClearSelection?: (f: Function) => Function;
  /** The rendered size of the input. */
  size?: TextInputProps['size'];
  /**
   * Optional app slug that when provided will filter that app out of the search results.
   * Used when you are searching within a specific context,
   * e.g. 1 app integration pages and you're within the context of Tortii,
   * we don't want Tortii to appear in search results for connecting to another app.
   */
  slugFilter?: string;
};

const emptyItem = {
  disabled: true,
  name: 'No search results found.',
};

const dedupeAndTrimWhitespace = (query: string) =>
  query.replace(/(\s+)/g, ' ').trim();

/**
 * A custom version of `Typeahead` which is intended for searching for and selecting a service.
 * Renders icons appropriate for each service.
 */
export const ServiceTypeahead = ({
  apiHostname,
  'aria-label': ariaLabel,
  disabledText,
  id,
  initialSelectedItem,
  inputId,
  isDisabled,
  labelId,
  menuAriaLabel,
  onChange,
  placeholder,
  setClearSelection,
  size,
  slugFilter,
  excludeUpcoming = true,
  includeIsAppAdmin = true,
  includeIsAppCollaborator = true,
  onResultsUpdate = () => {},
  renderFloatingMenu = (props: FloatingMenuProps) => (
    <FloatingMenu {...props} />
  ),
  resultsLimit = 4,
}: ServiceTypeaheadProps) => {
  const getIconUrlForItem = (item: any) => item.images.url_32x32;
  const getLabelForItem = (item: any) => item.name;

  const renderIcon = (item: any) => {
    if (!item || !item.images) {
      return null;
    }

    return (
      <span
        css={Styles.serviceIcon}
        data-disabled={isDisabled ? '' : undefined}
        data-zds
      >
        <ServiceIcon
          aria-hidden={true}
          serviceName={getLabelForItem(item)}
          size="small"
          src={getIconUrlForItem(item)}
        />
      </span>
    );
  };

  const { apps, loading, onSearch, searchQuery } = useAppSearchData({
    limit: resultsLimit,
    searchDebounceDelay: 300,
    excludeUpcoming,
    includeIsAppAdmin,
    includeIsAppCollaborator,
    apiHostname: apiHostname || '',
    onResultsUpdate,
  });

  const filteredApps = !slugFilter
    ? apps
    : apps.filter((app: any) => app.slug !== slugFilter);

  const hasZeroResults =
    loading === false && searchQuery.length > 0 && filteredApps.length === 0;

  const displayedApps = hasZeroResults ? [emptyItem] : filteredApps;

  return (
    <Typeahead
      aria-label={ariaLabel}
      autoComplete="off"
      disabledText={disabledText}
      getLabelForItem={getLabelForItem}
      id={id}
      initialSelectedItem={initialSelectedItem}
      inputId={inputId}
      isBusy={loading}
      isDisabled={isDisabled}
      // We are intentionally passing an empty `items` while loading previous
      // search results while searching. This prevents the user from clicking or
      // tapping on a result right after results suddenly change, which is a very
      // frustraing UX.
      //
      // Also passing empty `items` when the `searchQuery` is blank. This is
      // because `AppSearchBackend` returns a default set of apps when a
      // blank search query is specified. When the user type an initial
      // character into the `Typeahead`, these default apps render for a
      // split second, creating the impression of poor search results until
      // the GraphQL query returns.
      items={loading || searchQuery === '' ? [] : displayedApps}
      labelId={labelId}
      menuAriaLabel={menuAriaLabel}
      onChange={onChange}
      onInputValueChange={(query: string) =>
        onSearch(dedupeAndTrimWhitespace(query))
      }
      placeholder={placeholder}
      renderFloatingMenu={(menuProps) =>
        renderFloatingMenu({ ...menuProps, visibleItems: resultsLimit })
      }
      renderIcon={renderIcon}
      setClearSelection={setClearSelection}
      showEmptyList={true}
      size={size}
    />
  );
};
